import { useRef, useState } from "react";
import { Check, ChevronDown, Loader2, Save } from "lucide-react";
import { AD_ASPECT_RATIOS, type AspectRatio } from "@shared/aspectRatios";
import type { SizeDownloadItem } from "@shared/iteration-size-downloads";
import type { IterationSizeController } from "@/lib/iteration-size-downloads";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { invalidateGeneratedAdListings } from "@/lib/queryClient";

export function IterationSizeSaveControls({ setId, item, controller, alreadySaved, onSaveCurrent }: {
  setId: string; item: SizeDownloadItem; controller: IterationSizeController;
  alreadySaved: boolean; onSaveCurrent: () => Promise<boolean>;
}) {
  const [selected, setSelected] = useState<Set<AspectRatio>>(() => new Set([item.currentRatio]));
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const lock = useRef(false);
  const ratios = AD_ASPECT_RATIOS.filter(ratio => selected.has(ratio) && ratio !== item.currentRatio);
  const planned = ratios.filter(ratio => !controller.get(item.adId, ratio) || controller.get(item.adId, ratio)?.status === "failed").length;
  const canSave = (selected.has(item.currentRatio) && !alreadySaved) || ratios.length > 0;
  const blocked = busy || controller.busy || !canSave || planned > 0 && !confirmed;
  const save = async () => {
    if (lock.current || blocked) return;
    lock.current = true; setBusy(true); setError(undefined); setNotice(undefined);
    let count = 0;
    try {
      if (selected.has(item.currentRatio) && !alreadySaved) {
        if (!await onSaveCurrent()) throw new Error("The current size could not be saved. Retry saving it.");
        count++;
      }
      if (ratios.length) {
        await controller.run(setId, [item], ratios, true);
        const ready = ratios.filter(ratio => controller.get(item.adId, ratio)?.status === "done");
        if (ready.length) count += await controller.save(setId, [item], ready);
        const unfinished = ratios.filter(ratio => controller.get(item.adId, ratio)?.status !== "done");
        if (unfinished.length) setError(`Not saved: ${unfinished.join(", ")}. Failed renders can be retried; uncertain renders must be checked, not repeated.`);
      }
      if (count) {
        invalidateGeneratedAdListings();
        setNotice(`${count} size${count === 1 ? "" : "s"} saved to My Ads.`);
      }
    } catch (cause) {
      if (count) {
        invalidateGeneratedAdListings();
        setNotice(`${count} size${count === 1 ? "" : "s"} saved to My Ads.`);
      }
      setError(cause instanceof Error ? cause.message : "Save failed. Retry saving without repeating successful renders.");
    } finally { lock.current = false; setBusy(false); }
  };
  return <Popover>
    <PopoverTrigger asChild>
      <button type="button" onClick={event => event.stopPropagation()} data-testid={`button-save-image-${item.resultId}`}
        className="inline-flex min-w-0 flex-1 items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium hover:bg-muted">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 shrink-0" />}
        <span className="truncate">Save to My Ads</span><ChevronDown className="h-3 w-3 shrink-0" />
      </button>
    </PopoverTrigger>
    <PopoverContent align="start" className="w-[min(22rem,calc(100vw-1.5rem))]" onClick={event => event.stopPropagation()}>
      <h3 className="text-sm font-semibold">Save to My Ads in selected sizes</h3>
      <p className="mt-1 text-xs text-muted-foreground">Choose the current image or additional sizes. Other sizes use the My Ads reframe engine; only explicitly saved versions become library copies.</p>
      <div className="mt-2 flex gap-3 text-xs">
        <button type="button" className="underline" disabled={busy || controller.busy} onClick={() => { setSelected(new Set(AD_ASPECT_RATIOS)); setConfirmed(false); }}>Select all sizes</button>
        <button type="button" className="underline" disabled={busy || controller.busy} onClick={() => { setSelected(new Set()); setConfirmed(false); }}>Clear sizes</button>
      </div>
      <div className="my-3 space-y-2">
        {AD_ASPECT_RATIOS.map(ratio => {
          const current = ratio === item.currentRatio;
          const saved = current ? alreadySaved : !!controller.getSaved(item.adId, ratio);
          const status = current ? undefined : controller.get(item.adId, ratio)?.status;
          const id = `save-size-${item.resultId}-${ratio.replace(":", "x")}`;
          return <div key={ratio} className="flex items-center gap-2 text-xs">
            <Checkbox id={id} aria-label={`Save ${ratio}${current ? " current" : ""}`} checked={selected.has(ratio)}
              disabled={busy || controller.busy || (current && saved)} onCheckedChange={value => {
                setSelected(previous => { const next = new Set(previous); value === true ? next.add(ratio) : next.delete(ratio); return next; });
                setConfirmed(false); setError(undefined); setNotice(undefined);
              }} />
            <label htmlFor={id} className="flex-1">{ratio}{current && " — Current"}</label>
            <span className="text-muted-foreground">{saved ? <span className="inline-flex items-center gap-1"><Check className="h-3 w-3" />Saved</span> : status || (current ? "No render needed" : "Not requested")}</span>
          </div>;
        })}
      </div>
      <p className="text-xs" data-testid="save-size-render-count">{planned} new reframe request{planned === 1 ? "" : "s"} planned. Successful outputs are reused.</p>
      {planned > 0 && <label className="my-2 flex items-start gap-2 text-xs">
        <Checkbox aria-label="Confirm reframes for saving" checked={confirmed} disabled={busy || controller.busy} onCheckedChange={value => setConfirmed(value === true)} />
        <span>Generate the selected sizes and save them to My Ads.</span>
      </label>}
      <Button size="sm" className="mt-2 w-full" disabled={blocked} onClick={() => void save()} data-testid="button-save-selected-sizes">
        {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}Save selected sizes
      </Button>
      {ratios.some(ratio => ["uncertain", "running"].includes(controller.get(item.adId, ratio)?.status || "")) && <Button
        variant="outline" size="sm" className="mt-2 w-full" disabled={busy || controller.busy} onClick={() => {
          if (lock.current) return;
          lock.current = true; setBusy(true);
          void controller.checkStatus(setId, [item], ratios).catch(cause => setError(cause.message || "Could not check render status"))
            .finally(() => { lock.current = false; setBusy(false); });
        }}>Check render status</Button>}
      {notice && <p role="status" className="mt-2 text-xs text-emerald-700">{notice}</p>}
      {error && <p role="alert" className="mt-2 text-xs text-destructive">{error}</p>}
      {!!controller.saveFailures?.length && <ul className="mt-2 space-y-1 text-xs text-destructive">
        {controller.saveFailures.map((failure, index) => <li key={index}>{failure.name}: {failure.message}</li>)}
      </ul>}
    </PopoverContent>
  </Popover>;
}
